import { useState } from "react";
import { useUpdateCollectionMutation } from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { useCollection, useCollectionSlugCheck } from "@/api/hooks";
import { Button } from "../button";
import UrlNameField from "./UrlNameField";

export default function CollectionSlugInput() {
  const collection = useCollection();
  const [slug, setSlug] = useState<string>(collection?.slug || "");
  const [slugForCollectionId, setSlugForCollectionId] = useState(collection?.id);
  const [error, setError] = useState<string | null>(null);
  const [updateCollection, { isLoading }] = useUpdateCollectionMutation();

  if (collection?.id !== slugForCollectionId) {
    setSlugForCollectionId(collection?.id);
    setSlug(collection?.slug || "");
    setError(null);
  }

  const check = useCollectionSlugCheck(slug, collection?.id || "");

  if (!collection) return null;

  const owner = collection.owner_nickname || collection.user_id;
  const changed = check.normalized !== (collection.slug || "");
  const canSave = changed && check.state === "ok" && !isLoading;

  const handleSave = async () => {
    setError(null);
    try {
      await updateCollection({
        id: collection.id,
        title: collection.title,
        slug: check.normalized,
      }).unwrap();
    } catch (e) {
      setError(getApiErrorDetail(e, "Could not change the URL name. Please try again."));
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <UrlNameField
        id="collectionSlug"
        label="URL name"
        value={slug}
        onChange={setSlug}
        check={error ? { ...check, state: "error", message: error } : check}
        prefix={`${window.location.host}/${owner}/`}
        hint="Changing this keeps the old link working — it will redirect here."
        placeholder={collection.id}
      />
      <div className="flex justify-end">
        <Button
          type="button"
          variant="outline"
          disabled={!canSave}
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </div>
  );
}
